import { makeAutoObservable, runInAction } from 'mobx';

import {
  CONSENT_SELECTION,
  CONSENT_SELECTION_ACC,
  LoaderInitializationOptions,
} from '@types';

import i18n from '../i18n/config';
import { GTMService } from '../services/GTM/Gtm';
import { sendEventToHost } from '../utils/common';

export default class InitializeStore {
  token?: string;
  gaClientId?: string;
  consentCookie?: string;
  isLoading = true;
  error?: Error;
  options: LoaderInitializationOptions = {};
  showGenericAnimation?: boolean;

  constructor() {
    makeAutoObservable(this);

    sendEventToHost('loaded');
    window.addEventListener('message', this.handleMessage);

    if (import.meta.env.VITE_PARTNER_TOKEN) {
      runInAction(() => {
        const event = {
          token: import.meta.env.VITE_PARTNER_TOKEN,
          options: {
            campaignCallNumber: '(555) 555-5555',
            passthroughParams: { test_pass_param: 'test' },
            requestingHost: window.location.origin,
            // introUI: 'DCOM_01' as IntroTypes,
            language: import.meta.env.VITE_LANGUAGE ?? 'en',
            variant: import.meta.env.VITE_VARIANT,
            pid: import.meta.env.VITE_PID ?? '',
          },
        };
        this.token = import.meta.env.VITE_PARTNER_TOKEN;
        this.isLoading = false;
        this.options = event.options;
        this.showGenericAnimation = false;
        i18n.changeLanguage(event.options.language);
      });
    }
  }

  handleMessage = (event: MessageEvent<LoaderInitializationOptions>) => {
    if (event.data?.name === 'analyticsUpdate') {
      if (
        this.isLoading ||
        event.source !== window.parent ||
        event.origin !== this.options.requestingHost ||
        typeof event.data.gaClientId !== 'string' ||
        !event.data.gaClientId.trim()
      )
        return;
      this.gaClientId = event.data.gaClientId;
      GTMService.getInstance().setHostClientId(event.data.gaClientId);
      return;
    }
    if (event.data?.name === 'consentUpdate') {
      const { consentCookie } = event.data;
      this.consentCookie = consentCookie;
      if (consentCookie === CONSENT_SELECTION_ACC) {
        localStorage.setItem(CONSENT_SELECTION, consentCookie);
      }
    }
    if (event.data?.name === 'initialize') {
      const {
        dataToken,
        language,
        showGenericAnimation,
        gaClientId,
        consentCookie,
        ...options
      } = event.data;

      if (dataToken) {
        runInAction(() => {
          this.token = dataToken;
        });
      } else {
        runInAction(() => {
          this.error = new Error('Token not found');
        });
      }
      if (consentCookie) {
        runInAction(() => {
          this.consentCookie = consentCookie;
          if (consentCookie === CONSENT_SELECTION_ACC) {
            localStorage.setItem(CONSENT_SELECTION, consentCookie);
          }
        });
      }

      if (gaClientId) {
        runInAction(() => {
          this.gaClientId = gaClientId;
          GTMService.getInstance().setHostClientId(gaClientId);
        });
      }

      runInAction(() => {
        if (showGenericAnimation) {
          this.showGenericAnimation = showGenericAnimation;
        } else {
          this.showGenericAnimation = false;
        }
      });

      if (language && language !== i18n.language) {
        i18n.changeLanguage(language);
      }

      if (Object.keys(options).length) {
        // enforce PID to be a string
        if (options.pid) {
          options.pid = String(options.pid);
        }

        if (
          options.passthroughParams &&
          (options.passthroughParams as { language?: string }).language
        ) {
          options.passthroughParams = {
            ...options.passthroughParams,
            language: '',
          };
        }

        runInAction(() => {
          this.options = { ...options, language };
        });
      }

      runInAction(() => {
        this.isLoading = false;
      });
      // Request a replay after initialization: the host ID may have arrived while
      // this iframe was downloading, after the loader took its initial snapshot.
      sendEventToHost('analyticsReady');
    }
  };

  getVariant() {
    return this.options.variant || '';
  }
}
